<template>
  <div
    tabindex="0"
    class="duplicate-card-popover-wrap"
    @keydown.esc.stop="closePopover"
    @mousedown.stop
  >
    <div class="duplicate-card-popover-content">
      <div class="title-wrap">
        <vue-scroll
          :ops="{
            scrollPanel: { scrollingX: false },
            rail: { gutterOfSide: '4px', gutterOfEnds: '8px' },
            bar: { size: '4px', keepShow: true },
          }"
          class="title-input"
        >
          <AutoExpandTextarea
            :value="titleInput"
            :text-value="titleInput"
            :placeholder="translate('duplicateDefaultTitle')"
            :focus-field="!isMobile"
            :textarea-class="'duplicate-card-title'"
            :disable-resize="true"
            @input="titleInput = $event.target.value"
            @enter="duplicateCard"
            @esc="closePopover"
          />
        </vue-scroll>
      </div>
      <span class="duplicate-card-label usn">{{ translate('duplicateTo') }}</span>

      <!-- Project dropdown -->
      <span class="section-label usn">{{ translate('space') }}</span>
      <ListDropdownWrap
        ref="projectDropdown"
        :items="myProjects"
        :selected-item="selectedProjectIndex"
        @item-click="handleProjectClick"
      />

      <!-- Board dropdown -->
      <span class="section-label usn">{{ translate('board') }}</span>
      <ListDropdownWrap
        ref="boardDropdown"
        :items="boards"
        :selected-item="selectedBoardIndex"
        :empty-list-text="translate('noBoardsInSpace')"
        @item-click="handleBoardClick"
      />

      <!-- List dropdown -->
      <span class="section-label usn">{{ translate('list') }}</span>
      <ListDropdownWrap
        ref="listDropdown"
        :items="lists"
        :selected-item="selectedListIndex"
        :empty-list-text="translate('noListsInBoard')"
        @item-click="handleListClick"
      />
    </div>
    <div class="duplicate-button-wrap">
      <ButtonCustom
        :text="translate('duplicateCard')"
        :type="'important'"
        :button-dimensions="buttonDimensions"
        :class="{ disabled: disableAddButton }"
        @click="duplicateCard"
      />
    </div>
  </div>
</template>

<script>
import { nanoid } from 'nanoid';
import ButtonCustom from '@/components/elements/ButtonCustom';
import AutoExpandTextarea from '@/components/elements/AutoExpandTextarea';
import ListDropdownWrap from '@/components/widgets/ListDropdownWrap';
import getDashedTitle from '@/utilities/getDashedTitle';
import EventBus from '@/utilities/eventBus';
import { getSprintKey } from '@/utilities/sprintKeys';

export default {
  name: 'DuplicateCardPopup',
  components: {
    ButtonCustom,
    AutoExpandTextarea,
    ListDropdownWrap,
  },
  props: {
    cardId: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      defaultTitle: '',
      titleInput: '',
      selectedProjectIndex: -1,
      selectedBoardIndex: -1,
      selectedListIndex: -1,
      boards: [],
      lists: [],
      projectPosition: {},
      boardPosition: {},
      listPosition: {},
    };
  },
  computed: {
    buttonDimensions() {
      return { width: 290, height: 36 };
    },

    currentCard() {
      return this.$store.getters.getCards[this.cardId] || {};
    },

    cardTitle() {
      return this.currentCard.title;
    },

    myProjects() {
      return [...this.$store.getters.getMySortedProjects].map((project) => ({
        id: project.id,
        label: project.title,
      }));
    },

    currentProjectId() {
      return this.$store.getters.getCurrentProjectId || this.currentCard.project_id;
    },

    disableAddButton() {
      return (
        this.selectedProjectIndex === -1 ||
        this.selectedBoardIndex === -1 ||
        this.selectedListIndex === -1
      );
    },

    selectedProject() {
      return this.selectedProjectIndex !== -1 ? this.myProjects[this.selectedProjectIndex] : {};
    },

    selectedBoard() {
      return this.selectedBoardIndex !== -1 ? this.boards[this.selectedBoardIndex] : {};
    },

    selectedList() {
      return this.selectedListIndex !== -1 ? this.lists[this.selectedListIndex] : {};
    },

    currentBoard() {
      return this.$store.getters.currentBoard;
    },

    teamSlug() {
      return this.$store.getters.getTeamSlug;
    },

    isViewMyWorkOrMemberPage() {
      return [
        this.$constants.routeNames.view,
        this.$constants.routeNames.newView,
        this.$constants.myWorkRouteName,
        this.$constants.memberRouteName,
      ].includes(this.$route.name);
    },
  },
  mounted() {
    this.setDefaultTitle();
    this.setCurrentSettings();
  },
  methods: {
    closePopover() {
      this.$store.dispatch('setShowDuplicateCardPopup', '');
    },

    handleEnter() {
      this.duplicateCard();
    },

    setDefaultTitle() {
      const copyTitle = `${this.translate('duplicateOf')} - ${this.cardTitle}`;
      this.defaultTitle = copyTitle;
      this.titleInput = copyTitle;
    },

    setCurrentSettings() {
      this.selectedProjectIndex = this.myProjects.findIndex(
        (project) => project.id === this.currentProjectId
      );
      if (this.currentCard && this.currentBoard) {
        this.boards.push({
          id: this.currentBoard.id,
          label: this.currentBoard.title,
          lists: this.currentBoard.lists,
          list_order: this.currentBoard.list_order,
        });
        this.lists.push({
          id: this.currentCard.list_id,
          label: this.currentCard.list_title,
        });
        this.selectedBoardIndex = 0;
        this.selectedListIndex = 0;
      }
      this.fetchBoards(this.currentProjectId);
    },

    setDropdownsPosition() {
      const { projectDropdown, boardDropdown, listDropdown } = this.$refs;
      const gap = 8;
      if (projectDropdown) {
        const projectTop = projectDropdown.height + gap;
        this.projectPosition = {
          top: `${projectTop}px`,
          left: 0,
        };
      }
      if (boardDropdown) {
        const boardTop = boardDropdown.height + gap;
        this.boardPosition = {
          top: `${boardTop}px`,
          left: 0,
        };
      }
      if (listDropdown) {
        const listTop = listDropdown.height + gap;
        this.listPosition = {
          top: `${listTop}px`,
          left: 0,
        };
      }
    },

    updateCardIdInCardsOrder(localId, cardId, listId) {
      const listCards = [...this.$store.getters.getListCards[listId]];

      const index = listCards.indexOf(localId);
      if (index > -1) {
        listCards.splice(index, 1, cardId);
        this.$store.dispatch('setSingleListInListCards', {
          listId,
          value: listCards,
        });
      }
    },

    addCardToListCards(newCard, localId) {
      this.$store.dispatch('addCardToListCards', {
        listId: this.selectedList.id,
        cardId: localId,
      });
    },

    duplicateCard() {
      if (this.disableAddButton) return;
      const isSprintBoard = this.selectedBoard.id.includes('s');

      this.closePopover();
      const newCard = {
        title: this.titleInput || this.cardTitle,
        project_id: this.selectedProject.id,
        ...(isSprintBoard && { sprint_id: this.selectedBoard.id.split('s')[1] }),
        ...(!isSprintBoard && { board_id: this.selectedBoard.id }),
        list_id: isSprintBoard ? this.selectedList.orginalId : this.selectedList.id,
      };

      const localId = nanoid(6);

      this.$store.dispatch('addCardToListCards', {
        listId: this.selectedList.id,
        cardId: localId,
      });

      this.$store.dispatch('addCardToCards', {
        card: {
          ...newCard,
          ...(localId && { id: localId, localCardId: true }),
          list_title: this.selectedList.label,
          members: [],
          child_cards: [],
          child_card_order: [],
          checklists: [],
          checklist_order: [],
          checklist_item_order: {},
          parent_card: {},
          user_id: this.$store.getters.getUserId,
          tags: [],
        },
      });

      this.$store
        .dispatch('duplicateCard', {
          cardId: this.currentCard.id,
          newCard,
        })
        .then(({ card }) => {
          this.$store.dispatch('addUiNotification', {
            message: this.translate('cardDuplicated'),
            icon: 'copy',
            labels: [
              {
                text: this.translate('open'),
                type: this.$constants.uiNotificationElements.button,
                callback: this.openCard,
                params: card,
                buttonStyle: 'important',
              },
            ],
          });
          this.$store.dispatch('addCardToGroupByBoard', {
            boardId: card.board_id,
            card,
          });
          this.$store.dispatch('removeCardFromCards', localId);
          this.$store.dispatch('addCardToCards', {
            card,
          });
          this.updateCardIdInCardsOrder(localId, card.id, this.selectedList.id);
          this.addCardToCurrentViewResults(card);
        })
        .catch((/* err */) => {
          this.$store.dispatch('addUiNotification', {
            message: this.translate('cardDuplicateFailed'),
            status: this.$constants.uiNotificationStatuses.error,
            duration: 4000,
          });
        });
    },

    fetchBoards(projectId) {
      Promise.allSettled([
        this.$store.dispatch('loadBoards', projectId),
        this.$store.dispatch('getSprintsForProject', { projectId }),
      ]).then((results) => {
        const boardsResult = results[0];
        const sprintsResult = results[1];

        if (boardsResult.status === 'rejected' && sprintsResult.status === 'rejected') {
          return;
        }

        const boardsResponse = boardsResult.status === 'fulfilled' ? boardsResult.value : null;
        const sprintsResponse = sprintsResult.status === 'fulfilled' ? sprintsResult.value : null;

        if (!boardsResponse && !sprintsResponse) return;

        const boards = boardsResponse?.boards ?? [];
        const boardOrder = boardsResponse?.board_order ?? [];
        const sprints = sprintsResponse?.sprints ?? [];

        // Process boards
        this.boards = boards
          .sort((a, b) => boardOrder.indexOf(a.id) - boardOrder.indexOf(b.id))
          .map((board) => ({
            id: board.id,
            label: board.title,
            lists: board.lists,
            list_order: board.list_order,
          }));

        // Add sprints to boards array
        sprints.forEach((sprint) => {
          this.boards.push({
            id: sprint.id,
            label: sprint.title,
            lists: sprint.lists,
            list_order: sprint.lists.map((list) => list.orginalId),
          });
        });

        // Set selected board index
        if (!this.boards.length) {
          this.selectedBoardIndex = -1;
        } else {
          this.selectedBoardIndex = 0;
        }

        const boardIndex = this.boards.findIndex(
          (i) =>
            i.id === this.currentCard.board_id ||
            i.id === getSprintKey(this.currentCard.project_id, this.currentCard.sprint_id)
        );
        if (boardIndex !== -1) {
          this.selectedBoardIndex = boardIndex;
        }

        this.setLists();
      });
    },

    setLists(index = this.selectedBoardIndex) {
      const selectedBoard = this.boards[index];
      if (selectedBoard) {
        this.lists = selectedBoard.lists
          .sort(
            (a, b) =>
              selectedBoard.list_order.indexOf(a.id) - selectedBoard.list_order.indexOf(b.id)
          )
          .map((list) => ({
            id: list.id,
            label: list.title,
            ...(list.orginalId && { orginalId: list.orginalId }),
          }));
        if (!this.lists.length) {
          this.selectedListIndex = -1;
        } else {
          this.selectedListIndex = 0;
        }
        const listIndex = this.lists.findIndex((i) => i.id === this.currentCard.list_id);
        if (listIndex !== -1) {
          this.selectedListIndex = listIndex;
        }
      } else {
        this.lists = [];
        this.selectedListIndex = -1;
      }
    },

    handleProjectClick(item, index) {
      if (index !== -1 && index !== this.selectedProjectIndex) {
        this.fetchBoards(item.id);
        this.selectedProjectIndex = index;
      }
    },

    handleBoardClick(item, index) {
      if (index !== -1 && index !== this.selectedBoardIndex) {
        this.setLists(index);
        this.selectedBoardIndex = index;
      }
    },

    handleListClick(item, index) {
      this.selectedListIndex = index;
    },

    openCard(card) {
      if (card && Object.keys(card).length) {
        const dashedCardTitle = getDashedTitle(card.title);
        this.$store.dispatch('setDisplayedCards', [card.id]);
        this.$router.push({
          path: `/${this.teamSlug}/card-${card.id}-${dashedCardTitle}`,
          query: { userNavigated: true },
        });
      }
    },

    addCardToCurrentViewResults(card) {
      if (this.isViewMyWorkOrMemberPage) {
        EventBus.$emit('addCardToCurrentViewResults', card);
      }
    },

    hidePopup() {
      this.$store.dispatch('setShowDuplicateCardPopup', '');
    },
  },
};
</script>

<style lang="scss">
.duplicate-card-popover-wrap {
  pointer-events: all;
  background-color: $popoverBackground;
  width: 322px;
  border-radius: 8px;
  outline: none;
  z-index: 1;
  position: absolute;
  border: 1px solid;
  @apply border-important-border overflow-hidden;

  .duplicate-card-popover-content {
    padding: 16px 16px 8px;
    @apply bg-background-strongest text-text-default;

    .title-wrap {
      margin-bottom: 16px;

      .title-input {
        display: flex;
        flex-direction: column;
        overflow: hidden;
        min-height: 70px;
        max-height: 70px;
        background-color: $popoverBackground;
        border: 1px solid #dadcdf;
        box-sizing: border-box;
        border-radius: 8px;
        @apply bg-important-bg;

        textarea {
          @apply bg-important-bg text-text-default;
        }

        .__view {
          padding: 10px 14px;
        }
      }

      .duplicate-card-title {
        font-size: 16px;
        line-height: 22px;
        color: $gray900;
      }
    }

    .duplicate-card-label {
      display: flex;
      font-weight: 500;
      font-size: 18px;
      line-height: 21px;
      letter-spacing: 0.1px;
      margin-bottom: 16px;
      @apply text-text-default;
    }

    .section-label {
      display: flex;
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;
      margin-bottom: 6px;
      @apply text-text-default;
    }

    .dropdown-wrap {
      margin-bottom: 10px;
    }
  }
  .duplicate-button-wrap {
    padding: 0px 16px 16px;
    @apply bg-background-strongest;

    .button-wrap {
      .button-label {
        font-size: 14px;
        font-weight: 500;
        line-height: 20px;
      }
    }
  }
}
</style>
